import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, X } from 'lucide-react';

// Источники — первоисточники-исследования (журнал + год), кликабельны.
const SOURCES: { label: string; url: string }[] = [
  {
    label: 'Систематическое занижение самоотчётов в среднем на 27% — Nature Food, 2024',
    url: 'https://www.nature.com/articles/s43016-024-01089-5',
  },
  {
    label: 'Занижение: женщины ~32%, мужчины ~13% — Frontiers in Endocrinology, 2019',
    url: 'https://www.frontiersin.org/journals/endocrinology/articles/10.3389/fendo.2019.00850/full',
  },
  {
    label: 'Погрешность приложений (MyFitnessPal) 21–42% — Evenepoel et al., J Med Internet Res, 2020',
    url: 'https://doi.org/10.2196/18237',
  },
  {
    label: 'Сравнение 5 приложений, ошибки в количестве 13–17% — Griffiths et al., Public Health Nutrition, 2018',
    url: 'https://doi.org/10.1017/S1368980018000393',
  },
  {
    label: 'Ошибки в оценке размера порций 6–75% — J Med Internet Res, 2022',
    url: 'https://doi.org/10.2196/38283',
  },
  {
    label: 'Калорийность ресторанных блюд отклоняется до 48% — Urban et al., JAMA, 2011',
    url: 'https://www.sciencedaily.com/releases/2011/07/110719161538.htm',
  },
  {
    label: 'Самоконтроль питания связан со снижением веса — Burke et al., 2011; Patel et al., 2021',
    url: 'https://doi.org/10.1016/j.jada.2010.10.008',
  },
];

function openLink(url: string) {
  const tg = (window as any).Telegram?.WebApp;
  if (tg?.openLink) tg.openLink(url);
  else window.open(url, '_blank', 'noopener,noreferrer');
}

export function AccuracyNote() {
  const [open, setOpen] = useState(false);
  const [showSources, setShowSources] = useState(false);

  return (
    <>
      {/* Короткая строка под результатом */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 w-full text-left text-[11px] text-ink-dim leading-snug flex items-start gap-1.5 hover:text-ink transition-colors"
      >
        <Info size={13} className="shrink-0 mt-0.5 text-emerald-glow/70" />
        <span>
          Оценка примерная — как у любого подсчёта калорий. Это ок: важнее регулярность, чем граммы.{' '}
          <span className="text-emerald-glow underline underline-offset-2">Подробнее</span>
        </span>
      </button>

      {/* Модалка «Почему цифры примерные» */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              className="bg-bg w-full sm:max-w-md max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 border border-line/40"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="font-display text-lg leading-tight">
                  Почему цифры примерные — и почему это не важно
                </h3>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-ink-dim hover:text-ink shrink-0 p-1"
                  aria-label="Закрыть"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-3 text-sm text-ink-muted leading-relaxed">
                <p>
                  Любой подсчёт калорий — это оценка, а не аптечные весы. И дело даже не в приложении:
                  исследования показывают, что <span className="text-ink">люди сами занижают съеденное
                  в среднем на 20–30%</span>, независимо от инструмента. Распознавание по фото работает в
                  том же коридоре — это нормально.
                </p>
                <p className="text-ink font-medium">Точность до грамма тебе не нужна.</p>

                <div>
                  <p className="text-ink">🎯 Важен тренд, а не идеальная цифра.</p>
                  <p>
                    Записываешь примерно одинаково каждый день → видишь динамику. Именно она ведёт к
                    результату. А если вес не двигается 3–4 недели — значит, дефицита нет реально, и это ты
                    увидишь по трендам, а не по граммам.
                  </p>
                </div>

                <div>
                  <p className="text-ink">🔧 А хочешь точнее — вот инструменты:</p>
                  <ul className="mt-1 space-y-1">
                    <li>
                      📷 <span className="text-ink">Сфоткай состав на упаковке</span> — расчёт по официальным
                      цифрам производителя, самое точное для готовых продуктов.
                    </li>
                    <li>⚖️ Поправь вес порции, если ИИ промахнулся.</li>
                    <li>🍱 Раскрой сложное блюдо на составляющие.</li>
                    <li>🔍 Жми «Узнать пользу» — детальный разбор.</li>
                    <li>✍️ Или введи вручную, когда знаешь точно.</li>
                  </ul>
                </div>

                <div>
                  <p className="text-ink">Под каждую ситуацию — свой инструмент:</p>
                  <p>🍳 Домашняя еда, рестораны, всё без упаковки → фото «на глаз», тут важен тренд.</p>
                  <p>🏷️ Готовый продукт с этикеткой → фото состава, когда нужна точность.</p>
                </div>

                <div>
                  <p className="text-ink">💪 Но главное — привычка.</p>
                  <p>
                    Регулярный самоконтроль питания сам по себе связан со снижением веса — это доказанный
                    поведенческий эффект. Тот, кто записывает «примерно» каждый день, обгонит того, кто
                    считает идеально и бросает через неделю.
                  </p>
                </div>

                <p className="text-emerald-glow font-medium">
                  Не гонись за идеалом — гонись за регулярностью.
                </p>
              </div>

              {/* Источники */}
              <div className="mt-4 pt-3 border-t border-line/40">
                <button
                  type="button"
                  onClick={() => setShowSources((v) => !v)}
                  className="text-xs text-ink-dim hover:text-ink flex items-center gap-1"
                >
                  <span>{showSources ? '▾' : '▸'}</span> Источники исследований
                </button>
                {showSources && (
                  <ol className="mt-2 space-y-2 text-[11px] text-ink-dim list-decimal list-inside leading-snug">
                    {SOURCES.map((s) => (
                      <li key={s.url}>
                        <button
                          type="button"
                          onClick={() => openLink(s.url)}
                          className="text-left underline underline-offset-2 hover:text-emerald-glow"
                        >
                          {s.label}
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
